import _ from 'lodash'
import * as THREE from 'three'
import * as d3 from 'd3'

const domains = {
    exit_speed: [98, 108],
    distance: [380, 440],
    height: [50, 150],
    launch_angle: [20, 40],
    none: [0, 1],
}

function toDomain2(domain) {
    return domain
}

function toDomain3(domain) {
    return [domain[0], 0.5 * (domain[0] + domain[1]), domain[1]]
}

function HitPainter() {
    const colorDefault = '#666666'
    const colorLight = '#F0F7FF'
    const colorScaleDefault = d3
        .scaleLinear()
        .domain([0, 1])
        .range([colorDefault, colorDefault])
    const colorScaleLight = d3
        .scaleLinear()
        .domain([0, 1])
        .range([colorLight, colorLight])
    const cool = '#287EE1'
    const mid = '#FFFDFF'
    const // '#ffffff',
        warm = '#DC021B'
    const colorRamp3 = true // true: blue-white-red LAB, false: blue-red HCL
    const range2 = [cool, warm]
    const range3 = [cool, mid, warm]
    const range = colorRamp3 ? range3 : range2
    const toDomain = colorRamp3 ? toDomain3 : toDomain2
    // interpolator = d3.interpolateLab,
    const interpolator = d3.interpolateHcl
    const colorExitVelocity = d3
        .scaleLinear()
        .domain(toDomain(domains.exit_speed))
        .range(range)
        .interpolate(interpolator)
        .clamp(true)
    const colorDistance = d3
        .scaleLinear()
        .domain(toDomain(domains.distance))
        .range(range)
        .interpolate(interpolator)
        .clamp(true)
    const colorHeight = d3
        .scaleLinear()
        .domain(toDomain(domains.height))
        .range(range)
        .interpolate(interpolator)
        .clamp(true)
    const colorAngle = d3
        .scaleLinear()
        .domain(toDomain(domains.launch_angle))
        .range(range)
        .interpolate(interpolator)
        .clamp(true)

    const seed = d3
        .scaleOrdinal()
        .domain([1, 2, 3, 4, 5, 6, 7, 8])
        .range([
            '#4e79a7',
            '#f28e2c',
            '#e15759',
            '#76b7b2',
            '#59a14f',
            '#edc949',
            '#af7aa1',
            '#ff9da7',
            '#9c755f',
            '#bab0ab',
        ])

    this.scales = {
        default: colorScaleDefault,
        colorScaleLight,
        colorExitVelocity,
        colorDistance,
        colorHeight,
        colorAngle,
        seed,
    }
}

HitPainter.prototype.highlightRangeHandler = function (cb) {
    this.highlightRangeCB = cb

    return this
}

function seed(playData) {
    return playData.seed
}

function velocity(playData) {
    return playData.result.computedMetrics.exitVelocity.value
}

function distance(playData) {
    return playData.result.computedMetrics.projectedDistance.value
}

function height(playData) {
    const metrics = playData.result.computedMetrics
    const { maxHeight } = metrics

    if (maxHeight) {
        return maxHeight.value
    }

    const { path } = playData.result.hit
    const z = _.filter(path, (d, i) => (i + 1) % 3 === 0)

    return d3.max(z)
}

function launchAngle(playData) {
    const metrics = playData.result.computedMetrics
    const angle = metrics.launchAngle

    if (angle) {
        return angle.value
    }

    // otherwise, calculate angle
    const { path } = playData.result.hit
    // points = d3.range(0, path.length / 3).map(function (i) {
    // first 3 points - first 2 okay instead?
    const points = d3.range(3).map((i) => {
        const j = 3 * i

        return {
            x: path[j],
            y: path[j + 1],
            z: path[j + 2],
        }
    })
    const p0 = points[0]
    const p2 = points[2]
    const delta = (function (p, q) {
        return {
            x: q.x - p.x,
            y: q.y - p.y,
            z: q.z - p.z,
        }
    })(p0, p2)
    const d = Math.sqrt(delta.x * delta.x + delta.y * delta.y)
    const radians = Math.atan2(delta.z, d)

    return (radians * 180) / Math.PI
}

function noop() {
    return 1
}

function traceExtents(hits) {
    const speeds = hits.map((hit) => {
        const metrics = hit.userData.result.computedMetrics

        return metrics.exitVelocity.value
    })
    const extentSpeed = d3.extent(speeds)
    const distances = hits.map((hit) => {
        const metrics = hit.userData.result.computedMetrics

        return metrics.projectedDistance.value
    })
    const extentDistance = d3.extent(distances)
    const heights = hits.map((hit) => height(hit.userData))
    const extentHeights = d3.extent(heights)
    const angles = hits.map((hit) => launchAngle(hit.userData))
    const extentAngles = d3.extent(angles)

    console.log(`speeds - min: ${extentSpeed[0]}, max: ${extentSpeed[1]}`)
    console.log(
        `distance - min: ${extentDistance[0]}, max: ${extentDistance[1]}`
    )
    console.log(`height - min: ${extentHeights[0]}, max: ${extentHeights[1]}`)
    console.log(`angle - min: ${extentAngles[0]}, max: ${extentAngles[1]}`)
}

HitPainter.prototype.paint = function (hits, type) {
    // traceExtents(hits);'

    if (!hits) {
        return
    }

    let value = noop
    let scale = this.scales.default

    switch (type) {
        case 'exit_speed':
            value = velocity
            scale = this.scales.colorExitVelocity
            break
        case 'distance':
            value = distance
            scale = this.scales.colorDistance
            break
        case 'height':
            value = height
            scale = this.scales.colorHeight
            break
        case 'launch_angle':
            value = launchAngle
            scale = this.scales.colorAngle
            break
        case 'light':
            value = noop
            scale = this.scales.colorScaleLight
            break
        case 'seed':
            value = seed
            scale = this.scales.seed
            break
        case 'none':
        case 'round':
        default:
            value = noop
            scale = this.scales.default
    }

    hits.forEach((hit) => {
        const data = hit.userData
        const path = hit.getObjectByName('path')
        const ball = hit.getObjectByName('ball')

        const color = scale(value(data))

        path.material.color = new THREE.Color(color)
        // ball.material.color = new THREE.Color(color);
    })
}

function typeToDomain(type) {
    const domain = domains[type]
    const d1 = domain[0]
    const d2 = domain[1]
    const step = (d2 - d1) / 20

    return d3.range(d1, d2 + 1, step).reverse()
}

// TODOHI  render RGB for type: rounds
HitPainter.prototype.renderLegend = function (sel, type) {
    const that = this
    const legend = d3.select(sel)
    let svg
    let data
    let scale
    let itemSpan
    let halfItemSpan
    let items

    legend.selectAll('*').remove()

    // if (type === 'none' || type === 'round') {
    //     return
    // }
    //
    // if (!_.contains(['exit_speed', 'exit_speed', 'exit_speed', 'exit_speed'], type)) {
    // 	return
    // }

    switch (type) {
        case 'exit_speed':
            scale = this.scales.colorExitVelocity
            break
        case 'distance':
            scale = this.scales.colorDistance
            break
        case 'height':
            scale = this.scales.colorHeight
            break
        case 'launch_angle':
            scale = this.scales.colorAngle
            break
        default:
            return
    }

    data = typeToDomain(type)

    itemSpan = data[0] - data[1]
    halfItemSpan = 0.5 * itemSpan

    svg = legend
        .append('svg')
        .attr('width', 70)
        .attr('height', '100%')
        .attr('viewBox', '0 0 70 220')

    items = svg
        .selectAll('rect.item')
        .data(data)
        .enter()
        .append('rect')
        .classed('item', true)
        .attr('data-type', type)
        .attr('data-spanIn', (d, i) => {
            let spanIn = d - halfItemSpan

            if (i === data.length - 1) {
                spanIn = Number.NEGATIVE_INFINITY
            }

            return spanIn
        })
        .attr('data-spanOut', (d, i) => {
            let spanOut = d + halfItemSpan

            if (i === 0) {
                spanOut = Number.POSITIVE_INFINITY
            }

            return spanOut
        })
        .attr('x', 0)
        .attr('y', (d, i) => 10 * i)
        .attr('width', 20)
        .attr('height', 10)
        .style('fill', (d) => scale(d))

    // TODO    might be more appropriate to just relay the event and data to ui and let it decide how to handle
    function highlightItem(d, i) {
        const item = d3.select(this)
        const type = item.attr('data-type')
        const spanIn = +item.attr('data-spanIn')
        const spanOut = +item.attr('data-spanOut')

        that.highlightRangeCB && that.highlightRangeCB(type, spanIn, spanOut)
    }

    items
        .on('mouseover', highlightItem)
        .on('click', highlightItem)
        .on('mouseleave', (d) => {
            that.highlightRangeCB && that.highlightRangeCB()
        })

    svg.selectAll('text')
        .data(data)
        .enter()
        .append('text')
        .text((d, i) => {
            const include = i === 0 || i === 10 || i === 20

            if (include) {
                switch (type) {
                    case 'exit_speed':
                        return `${d} mph`
                    case 'distance':
                        return `${d} ft`
                    case 'height':
                        return `${d} ft`
                    case 'launch_angle':
                        return `${d} °`
                    default:
                        return d
                }
            }
        })
        .attr('x', 25)
        .attr('y', (d, i) => 8 + 10 * i)
}

export default HitPainter
